var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { Button, Flex, Typography, theme } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import { useTranslation } from 'react-i18next';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useFormContext } from 'react-hook-form';
import { useCreateEventContext } from '../composers/CreateEventContext';
import { useFooterValidation } from '../hooks/useFooterValidation';
import { useState } from 'react';
import moment from 'moment';
import { useConfig } from '../context/ConfigContext';
import { ComposerAnalyticsEvent, ComposerMode, useSafeFormData, } from '../types/index';
import { useEventComposerGlobalContext } from '../composers/EventComposerGlobalContext';
import { buildCreateLegacyComposerUrl } from '../utils/redirectToLegacyComposer';
export var CreateFooterButtons = function () {
    var t = useTranslation('meetingRooms').t;
    var token = theme.useToken().token;
    var _a = useConfig(), environmentUrls = _a.environmentUrls, userContext = _a.userContext;
    var dashboardUrl = environmentUrls.dashboardUrl;
    var currentUser = userContext.currentUser, currentOrg = userContext.currentOrg;
    var formData = useSafeFormData();
    var startTime = formData.startTime, endTime = formData.endTime, invitees = formData.invitees, title = formData.title, isPrivateEvent = formData.isPrivateEvent, uid = formData.uid, isAllDayEvent = formData.isAllDayEvent, attendeeCount = formData.attendeeCount, spaces = formData.spaces;
    var _b = useEventComposerGlobalContext(), timezone = _b.timezone, onTrackComposerEvent = _b.onTrackComposerEvent;
    // Read during render so react-hook-form's formState proxy subscribes —
    // a click-time-only read could return a stale value.
    var formState = useFormContext().formState;
    var formIsDirty = formState.isDirty;
    var _c = useCreateEventContext(), handleEventCreation = _c.handleEventCreation, creatingEvent = _c.creatingEvent, createEventError = _c.createEventError;
    var _d = useFeatureFlags(), checkFlag = _d.checkFlag, featureFlagsLoading = _d.loading;
    var showMoreOptionsButton = !featureFlagsLoading && !checkFlag('hydra(shell-event-composer)');
    // The new-composer world keeps the legacy redirect as a compact icon
    // button. Staging infra redirects the legacy URL back to the sidebar
    // composer, so the button only round-trips there; it works in prod.
    var showLegacyComposerIconButton = !featureFlagsLoading && checkFlag('hydra(shell-event-composer)');
    var _e = __read(useState(false), 2), isRedirectingToOldComposer = _e[0], setIsRedirectingToOldComposer = _e[1];
    var _f = useFooterValidation({
        onSubmit: handleEventCreation,
    }), showValidationError = _f.showValidationError, handleClick = _f.handleClick, isSpaceRequestOnlyEvent = _f.isSpaceRequestOnlyEvent, isCheckingConflicts = _f.isCheckingConflicts;
    var buttonLabel = isSpaceRequestOnlyEvent
        ? t('create_event.request')
        : t('create_event.save');
    // `isDirty` answers "did they edit the form before switching, or bail
    // immediately".
    var trackSwitchToLegacyComposer = function () {
        onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.SWITCHED_TO_LEGACY_COMPOSER, {
            mode: ComposerMode.CREATE,
            isDirty: formIsDirty,
        });
    };
    // Carries the LIVE form values so the legacy composer opens with the
    // user's current (unsaved) changes.
    var buildLegacyUrl = function () {
        var _a;
        return buildCreateLegacyComposerUrl({
            dashboardUrl: dashboardUrl,
            title: title,
            isPrivateEvent: isPrivateEvent,
            orgId: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.id,
            startTime: startTime,
            endTime: endTime,
            spaceId: (_a = spaces === null || spaces === void 0 ? void 0 : spaces[0]) === null || _a === void 0 ? void 0 : _a.id,
            redirectTo: window.location.href,
            uid: uid,
            timezone: timezone || moment.tz.guess(),
            isAllDayEvent: isAllDayEvent,
            invitees: invitees,
            attendeeCount: attendeeCount,
            isSpaceRequestOnlyEvent: isSpaceRequestOnlyEvent,
            currentUserId: currentUser === null || currentUser === void 0 ? void 0 : currentUser.id,
        });
    };
    // Flag-off wide button: full-page navigation (legacy behavior). Tracking
    // fires before the redirect — a hard navigation can lose trailing events.
    var redirectToOldComposer = function () {
        setIsRedirectingToOldComposer(true);
        trackSwitchToLegacyComposer();
        window.location.href = buildLegacyUrl();
    };
    // Icon button: new tab — this composer stays open, so no redirecting
    // state and no loading spinner.
    var openOldComposerInNewTab = function () {
        trackSwitchToLegacyComposer();
        window.open(buildLegacyUrl(), '_blank', 'noopener,noreferrer');
    };
    return (_jsxs(FooterWrapper, { children: [showValidationError && (_jsx(Flex, { align: "center", gap: "4px", children: _jsx(ErrorText, { style: { color: token.colorError }, children: t('create_event.form_has_errors') }) })), _jsxs(BottomRowButtons, { children: [_jsx(Button, { variant: "dashed", type: "primary", size: "large", style: { width: '100%', flex: 1 }, "data-survey-trigger": "create-event", onClick: handleClick, loading: (creatingEvent && !createEventError) || isCheckingConflicts, disabled: creatingEvent || isRedirectingToOldComposer, children: buttonLabel }), showMoreOptionsButton && (_jsx(Button, { variant: "dashed", size: "large", style: { width: '100%', flex: 1 }, onClick: redirectToOldComposer, loading: isRedirectingToOldComposer && !createEventError, disabled: creatingEvent, children: t('create_event.more_options') })), showLegacyComposerIconButton && (_jsx(Button, { size: "large", "aria-label": t('create_event.open_full_composer'), icon: _jsx(FontAwesomeIcon, { icon: faArrowUpRightFromSquare }), onClick: openOldComposerInNewTab, disabled: creatingEvent }))] })] }));
};
var FooterWrapper = styled(Flex)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n"])));
var BottomRowButtons = styled(Flex)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  gap: 8px;\n"], ["\n  display: flex;\n  flex-direction: row;\n  gap: 8px;\n"])));
var ErrorText = styled(Typography.Text)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: block;\n"], ["\n  display: block;\n"])));
var templateObject_1, templateObject_2, templateObject_3;
